import { computed, ref, type ComputedRef, type Ref } from 'vue'

import { billingApi } from '../api/BillingApi'
import type {
  ContinueOnFreeResult,
  PaymentInvoiceDto,
  PricingQuote,
  SubscriptionInvoice,
} from '../models/billing.types'
import {
  canManageSubscription,
  nextRenewalDateFromInvoices,
  normalizeInvoiceHistory,
  subscriptionField,
} from '../service/subscriptionPresentation'

import { getAccountDetailPackageId, useAccount, useRbac } from '@/features/account'

export interface UseSubscription {
  quote: Ref<PricingQuote | null>
  rawInvoices: Ref<PaymentInvoiceDto[]>
  invoices: ComputedRef<SubscriptionInvoice[]>
  renewalAt: ComputedRef<string | null>
  planName: ComputedRef<string>
  currency: ComputedRef<string>
  canManage: ComputedRef<boolean>
  loading: Ref<boolean>
  mutating: Ref<boolean>
  error: Ref<unknown>
  load: (force?: boolean) => Promise<void>
  cancel: () => Promise<void>
  continueOnFree: () => Promise<ContinueOnFreeResult>
}

export function useSubscription(): UseSubscription {
  const account = useAccount()
  const rbac = useRbac()
  const quote = ref<PricingQuote | null>(null)
  const rawInvoices = ref<PaymentInvoiceDto[]>([])
  const loading = ref(false)
  const mutating = ref(false)
  const error = ref<unknown>(null)
  let loadedAccountId: number | undefined

  const currency = computed(() =>
    String(
      quote.value?.currency ||
        subscriptionField(account.accountDetail.value, ['currency']) ||
        'USD',
    ),
  )
  const invoices = computed(() => normalizeInvoiceHistory(rawInvoices.value, currency.value))
  const renewalAt = computed(() => nextRenewalDateFromInvoices(rawInvoices.value))
  const planName = computed(() =>
    String(
      subscriptionField(account.accountDetail.value, ['plan_name', 'package_name', 'name']) || '',
    ),
  )
  const canManage = computed(() =>
    canManageSubscription({
      accountUserId: account.accountUserId.value,
      userId: account.userId.value,
      selectedAccountLevel: account.accountLevel.value,
      isSysAdmin: rbac.isSysAdmin.value,
    }),
  )

  async function load(force = false): Promise<void> {
    const accountId = account.accountId.value
    if (accountId === undefined || (!force && loadedAccountId === accountId)) return
    loading.value = true
    error.value = null
    try {
      const [latestQuote, history] = await Promise.all([
        billingApi.getLatestPricingQuote(accountId).catch(() => null),
        billingApi.listInvoices(accountId),
      ])
      quote.value = latestQuote
      rawInvoices.value = history
      loadedAccountId = accountId
    } catch (cause) {
      error.value = cause
      throw cause
    } finally {
      loading.value = false
    }
  }

  async function cancel(): Promise<void> {
    const accountId = account.accountId.value
    const packageId = getAccountDetailPackageId(account.accountDetail.value)
    if (accountId === undefined || packageId === null) throw new Error('Missing billing context')
    mutating.value = true
    try {
      await billingApi.cancelSubscription(accountId, packageId)
      await account.loadSelectedAccountContext(true)
    } finally {
      mutating.value = false
    }
  }

  async function continueOnFree(): Promise<ContinueOnFreeResult> {
    const accountId = account.accountId.value
    if (accountId === undefined) throw new Error('Missing billing context')
    mutating.value = true
    try {
      const result = await billingApi.continueOnFree(accountId)
      await account.loadSelectedAccountContext(true)
      return result
    } finally {
      mutating.value = false
    }
  }

  return {
    quote,
    rawInvoices,
    invoices,
    renewalAt,
    planName,
    currency,
    canManage,
    loading,
    mutating,
    error,
    load,
    cancel,
    continueOnFree,
  }
}
